import type { ReportItem, ReportTable, SimulationReport } from "./report";
import { formatCell } from "./report";

const TITLE_COLOR = "664F4E";
const MUTED_COLOR = "656565";
const HEADER_FILL = "E9E1E2";

/** Document Word de la simulation affichée, mis en forme comme l'écran (montants et pourcentages en français). */
export async function buildWordDocument(report: SimulationReport) {
	const { AlignmentType, Document, HeadingLevel, Paragraph, ShadingType, Table, TableCell, TableRow, TextRun, WidthType } = await import("docx");

	const paragraph = (content: string, { size = 20, color, bullet = false }: { size?: number; color?: string; bullet?: boolean } = {}) =>
		new Paragraph({ spacing: { after: 100 }, ...(bullet ? { bullet: { level: 0 } } : {}), children: [new TextRun({ text: content, size, ...(color ? { color } : {}) })] });

	const heading = (content: string) =>
		new Paragraph({
			heading: HeadingLevel.HEADING_2,
			spacing: { before: 320, after: 120 },
			keepNext: true,
			children: [new TextRun({ text: content, bold: true, size: 26, color: TITLE_COLOR })],
		});

	const cell = (content: string, { header = false, numeric = false, size = 18, color }: { header?: boolean; numeric?: boolean; size?: number; color?: string }) =>
		new TableCell({
			...(header ? { shading: { type: ShadingType.CLEAR, color: "auto", fill: HEADER_FILL } } : {}),
			margins: { top: 40, bottom: 40, left: 80, right: 80 },
			children: [
				new Paragraph({
					alignment: numeric ? AlignmentType.RIGHT : AlignmentType.LEFT,
					children: [new TextRun({ text: content, bold: header, size, ...(color ? { color } : {}) })],
				}),
			],
		});

	const table = ({ columns, rows }: ReportTable, size = 18) => {
		if (!rows.length) return paragraph("Non renseignée");
		const numeric = columns.map((_, index) => rows[0]![index]?.type !== "text");
		return new Table({
			width: { size: 100, type: WidthType.PERCENTAGE },
			rows: [
				new TableRow({ tableHeader: true, children: columns.map((label, index) => cell(label, { header: true, numeric: numeric[index], size })) }),
				...rows.map((values) => new TableRow({ cantSplit: true, children: values.map((value) => cell(formatCell(value), { numeric: value.type !== "text", size })) })),
			],
		});
	};

	const items = (list: ReportItem[]) => {
		const withNotes = list.some((item) => item.note);
		return new Table({
			width: { size: 100, type: WidthType.PERCENTAGE },
			rows: list.map(
				(item) =>
					new TableRow({
						cantSplit: true,
						children: [
							cell(item.label, { size: 20 }),
							cell(formatCell(item.value), { numeric: item.value.type !== "text", size: 20 }),
							...(withNotes ? [cell(item.note ?? "", { size: 18, color: MUTED_COLOR })] : []),
						],
					}),
			),
		});
	};

	return new Document({
		creator: "Wenimmo",
		title: report.title,
		description: report.summary,
		styles: { default: { document: { run: { font: "Calibri", size: 20 } } } },
		sections: [
			{
				properties: { page: { margin: { top: 1000, bottom: 1000, left: 1000, right: 1000 } } },
				children: [
					new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: report.title, bold: true, size: 40, color: TITLE_COLOR })] }),
					paragraph(report.summary),
					paragraph(report.generatedLabel, { color: MUTED_COLOR }),
					heading("Hypothèses"),
					items(report.hypotheses),
					heading("Résultats clés"),
					items(report.keyFigures),
					heading("Tableau annuel"),
					table(report.annual, 16),
					heading("Allocation retenue"),
					table(report.allocation),
					heading("Frais par SCPI"),
					table(report.fees),
					heading("Total des frais de la simulation"),
					items(report.feeTotals),
					heading("Sources des données"),
					table(report.sources),
					heading("Répartition géographique"),
					table(report.geography),
					heading("Répartition sectorielle"),
					table(report.sectors),
					...(report.warnings.length ? [heading("Avertissements"), ...report.warnings.map((warning) => paragraph(warning, { bullet: true }))] : []),
					heading("À savoir"),
					...report.notes.map((note) => paragraph(note, { size: 18, color: MUTED_COLOR })),
				],
			},
		],
	});
}

export async function buildWordBlob(report: SimulationReport): Promise<Blob> {
	const { Packer } = await import("docx");
	return Packer.toBlob(await buildWordDocument(report));
}
